import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCliLoginStatus,
  listCliLogins,
  type CliLogin,
  type CliLoginCli,
  type CliLoginStatus,
} from "@/desktop/cli-logins";

export interface CliLoginsState {
  /** `null` until the first read finishes. */
  logins: CliLogin[] | null;
  /** Per login id; missing while its check runs, `null` when it failed. */
  statuses: Readonly<Record<string, CliLoginStatus | null>>;
  /** The default login's status, the same way; only with `withStatus`. */
  defaultStatus: CliLoginStatus | null | undefined;
  readFailed: boolean;
  reload: () => Promise<void>;
}

/**
 * This computer's added logins for one CLI, re-read whenever the window
 * gains focus (a sign-in usually finishes in the browser). `withStatus`
 * also checks each login's sign-in and account, the default login's too.
 */
export function useCliLogins(
  cli: CliLoginCli,
  { withStatus }: { withStatus: boolean },
): CliLoginsState {
  const [logins, setLogins] = useState<CliLogin[] | null>(null);
  const [statuses, setStatuses] = useState<
    Record<string, CliLoginStatus | null>
  >({});
  const [defaultStatus, setDefaultStatus] = useState<
    CliLoginStatus | null | undefined
  >(undefined);
  const [readFailed, setReadFailed] = useState(false);
  const revision = useRef(0);

  const reload = useCallback(async () => {
    const request = ++revision.current;
    const current = () => revision.current === request;
    let next: CliLogin[];
    try {
      next = (await listCliLogins()).filter((login) => login.cli === cli);
    } catch {
      if (current()) setReadFailed(true);
      return;
    }
    if (!current()) return;
    setLogins(next);
    setReadFailed(false);
    if (!withStatus) return;
    const check = (id: string | null) =>
      getCliLoginStatus(id).catch(() => null);
    const [byDefault, checked] = await Promise.all([
      check(null),
      Promise.all(
        next.map(async (login) => [login.id, await check(login.id)] as const),
      ),
    ]);
    if (!current()) return;
    setDefaultStatus(byDefault);
    setStatuses(Object.fromEntries(checked));
  }, [cli, withStatus]);

  useEffect(() => {
    const refresh = () => void reload();
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      revision.current += 1;
      window.removeEventListener("focus", refresh);
    };
  }, [reload]);

  return { logins, statuses, defaultStatus, readFailed, reload };
}
